import kb, {collapseWhitespace} from './kb'
import type {SegmentData} from './models/Segment'
import type {BookingStopData} from './models/Stop'
import type {StopPosition} from './components/Stop'
import {appendQueryParams} from './helpers/ajax'
import {jsonStringify} from './helpers/jsonSerializable'

export interface ShowBookingSegment {
    id: number;
    pickup_time: string;
    date: string;
    time: string;
    duration: number;
    comments: string;
    booking_id: number;
    driver_id: number;
    vehicle_id: number;
    rate: unknown;
    stops: ShowBookingStop[];
}

interface ShowBookingStop {
    id: number;
    city: string;
    postal_code: string;
    place_name: string;
    /** 2-letter country code */
    country: string;
    province: string;
    street: string;
    segment_id: number;
    order: number;
}

export interface AddressBookingStop {
    street: string
    placeName: string
    city: string
    province: string
    postalCode: string
}

// TODO: consolidate all of these different Stop types.
export type AnyKindOfStop = XOR<AddressBookingStop|ShowBookingStop|BookingStopData|FormattedStop>

// https://stackoverflow.com/questions/69059418/typescript-suppress-property-does-not-exist-on-type-for-union-types
export function formatAddress(stop: AnyKindOfStop): string {
    let address = [stop.street || stop.placeName || stop.place_name, stop.city, stop.province]
        .map(s => collapseWhitespace(s))
        .filter(s => s)
        .join(', ');
    const postalCode = collapseWhitespace(stop.postalCode || stop.postal_code)
    if (postalCode) {
        address += ` ${postalCode}`;
    }
    return address;
}

export function formatSegments(segments: SegmentData[]|ShowBookingSegment[]) {
    return segments.map((seg, i) => ({
        number: i + 1,
        pickupTime: seg.pickup_time,
        duration: seg.duration,
        stops: seg.stops.map((stop, j) => formatBookingStop(stop, j, seg.stops.length)),
    }));
}

export function formatBookingStop(stop: ShowBookingStop|BookingStopData, index: number, numStops: number) {
    let position: StopPosition = 'ST'
    if(index === 0) {
        position = 'PU'
    } else if(index === numStops - 1) {
        position = 'DO'
    }
    return {
        letter: kb.columnName(index),
        position,
        city: collapseWhitespace(stop.city),
        postalCode: collapseWhitespace(stop.postal_code),
        province: collapseWhitespace(stop.province),
        street: collapseWhitespace(stop.street),
        placeName: collapseWhitespace(stop.place_name),
        country: collapseWhitespace(stop.country),
    }
}

export type FormattedStop = {
    letter: string
    position: string
    city: string
    postalCode: string
    province: string
    street: string
    placeName: string
    country: string
}

export type FormattedSegments = ReturnType<typeof formatSegments>
export type FormattedSegment = FormattedSegments[number]

export const GOOGLE_MAP_COLORS = [
    '#F44336',
    '#2196F3',
    '#8BC34A',
    '#9C27B0',
    '#FF9800',
    '#009688',
    '#E91E63',
    '#00BCD4',
    '#3F51B5',
]

export function getGoogleMapColor(idx: number) {
    return GOOGLE_MAP_COLORS[idx % GOOGLE_MAP_COLORS.length]
}

export function stopIsValid({city, postalCode, province, street, placeName, country}: StopAddress) {
    return ((city && province) || postalCode)
        && (street || placeName)
        && country?.length === 2
}

export function makeGoogleMapsUrl(seg: SegmentData) {
    // const waypoints = seg.stops.map(s => encodeURIComponent(formatAddress(s)))
    // const data: string[] = [
    //     '3m1',
    //     '4b1',
    //     '4m18',
    //     '4m17',
    //     '1m5',
    //     '1m1',
    //     '1s0x808f7991cddd85a1:0x1a28f4e85e974239',
    //     '2m2',
    //     '1d-122.4345677',
    //     '2d37.640741',
    //     '1m5',
    //     '1m1',
    //     '1s0x808f79970a92b669:0x274d4fdb85a1c710',
    //     '2m2',
    //     '1d-122.4270161',
    //     '2d37.6444502',
    // ]
    // const pickupDateTime = combineDateAndTime(seg.date, seg.time)
    // if(pickupDateTime) {
    //     console.log(pickupDateTime)
    //     data.push('2m3','6e0','7e2','8j'+Math.floor(pickupDateTime.valueOf()/1000))
    // }
    // data.push('3e0')
    // return 'https://www.google.com/maps/dir/' + waypoints.join('/') + '/data=' + data.map(d => '!'+d).join('')

    const waypoints = seg.stops.map(s => formatAddress(s))
    let origin = ''
    let destination = '';
    if(waypoints.length) {
        origin = waypoints.shift()!
        if(waypoints.length) {
            destination = waypoints.pop()!
        }
    }
    const params: Record<string,string|number> = {
        api: 1,
        travelmode: 'driving',
        origin,
        destination,
    }
    if(waypoints.length) {
        params.waypoints = waypoints.join('|')
    }
    // const pickupDateTime = combineDateAndTime(seg.date, seg.time)
    // if(pickupDateTime) {
    //     params.departureTime = Math.floor(pickupDateTime.valueOf()/1000)
    // }
    return appendQueryParams('https://www.google.com/maps/dir/', params)
}

/**
 * Stringify stop used for caching.
 */
export function makeStopKey({placeName, street, city, province, postalCode, country}: StopAddress) {
    return jsonStringify([placeName, street, city, province, postalCode, country].map(s => s.toLowerCase()))
}

export type StopAddress = {
    city: string
    postalCode: string
    province: string
    street: string
    placeName: string
    country: string
}

export function getStopAddress({city, postalCode, province, street, placeName, country}: StopAddress): StopAddress {
    return {city, postalCode, province, street, placeName, country}
}
